import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useRoute } from 'wouter';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useToast } from '@/hooks/use-toast';
import {
  ShoppingCart, Loader2, Plus, Trash2, ArrowLeft,
  Mail, MapPin, Phone, AlertTriangle, Save, Send,
  FileText, RefreshCw, CheckCircle, Download,
  Search,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface Address {
  name?: string;
  company?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

interface PurchaseOrder {
  id: string;
  storeId: string;
  poNumber: string;
  status: string;
  notes: string | null;
  sourceOrderName: string | null;
  poSupplierId: string | null;
  shipToAddress: Address | null;
  supplierNameSnapshot: string | null;
  supplierEmailSnapshot: string | null;
  supplierAddressSnapshot: Address | null;
  supplierDeletedNote: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
}

interface LineItem {
  id?: string;
  productTitle: string;
  variantTitle: string;
  sku: string;
  quantity: number;
  unitPriceCents: number;
  /** local-only key for React keys */
  _key?: string;
}

interface PoSupplier {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  supplierAddress: Address | null;
  autoSend: boolean;
  dropshipMode: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function centsToDisplay(cents: number): string {
  return (cents / 100).toFixed(2);
}

function displayToCents(val: string): number {
  const n = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(n)) return 0;
  return Math.round(n * 100);
}

function addressOneLine(addr: Address | null): string {
  if (!addr) return '';
  const parts = [addr.line1, addr.line2, addr.city, addr.state, addr.postcode, addr.country].filter(Boolean);
  return parts.join(', ');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

let keyCounter = 0;
function nextKey() { return `item-${++keyCounter}`; }

function blankItem(): LineItem {
  return { productTitle: '', variantTitle: '', sku: '', quantity: 1, unitPriceCents: 0, _key: nextKey() };
}

// ── Inventory badge pill ───────────────────────────────────────────────────────

function InventoryBadge({ qty }: { qty: number }) {
  if (qty > 5) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 shrink-0">
        {qty} in stock
      </span>
    );
  }
  if (qty > 0) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0">
        {qty} in stock
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border border-border shrink-0">
      Out of stock
    </span>
  );
}

// ── Product search combobox ───────────────────────────────────────────────────

interface ShopifyVariant {
  id: number;
  title: string;
  sku: string | null;
  inventoryQuantity: number;
  price: string | null;
}

interface ShopifyProduct {
  id: number;
  title: string;
  variants: ShopifyVariant[];
}

interface ProductSearchComboboxProps {
  value: string;
  storeId: string;
  token: string | null;
  onChange: (val: string) => void;
  onSelect: (productTitle: string, variantTitle: string, sku: string) => void;
}

function ProductSearchCombobox({ value, storeId, token, onChange, onSelect }: ProductSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ShopifyProduct[]>([]);
  const [noResults, setNoResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, '');

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setNoResults(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    try {
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${BASE_URL}/api/po/products/search?storeId=${encodeURIComponent(storeId)}&q=${encodeURIComponent(q)}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      const products: ShopifyProduct[] = data.products ?? [];
      setResults(products);
      setNoResults(products.length === 0);
      setOpen(true);
    } catch {
      // silently ignore
    } finally {
      setSearching(false);
    }
  }, [storeId, token, BASE_URL]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  }

  function handleSelect(product: ShopifyProduct, variant: ShopifyVariant) {
    const variantTitle = variant.title === 'Default Title' ? '' : variant.title;
    onSelect(product.title, variantTitle, variant.sku ?? '');
    setOpen(false);
    setResults([]);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Product name"
          className="w-full text-sm bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1.5 py-1 text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors pr-6"
        />
        {searching && (
          <Loader2 className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-muted-foreground pointer-events-none" />
        )}
        {!searching && value.length >= 2 && (
          <Search className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/40 pointer-events-none" />
        )}
      </div>

      {open && (results.length > 0 || noResults) && (
        <div className="absolute z-50 left-0 top-full mt-1 w-72 max-h-72 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg py-1">
          {noResults ? (
            <div className="px-3 py-4 text-xs text-center text-muted-foreground">No products found</div>
          ) : (
            results.map((product) => (
              <div key={product.id}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/30 sticky top-0">
                  {product.title}
                </div>
                {product.variants.map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(product, variant); }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors text-left"
                  >
                    <span className="text-foreground truncate">
                      {variant.title === 'Default Title' ? product.title : variant.title}
                      {variant.sku && (
                        <span className="text-muted-foreground ml-1">· {variant.sku}</span>
                      )}
                    </span>
                    <InventoryBadge qty={variant.inventoryQuantity} />
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Address editor ─────────────────────────────────────────────────────────────

function AddressEditor({ value, onChange, label }: {
  value: Address;
  onChange: (a: Address) => void;
  label: string;
}) {
  function field(k: keyof Address) {
    return (
      <input
        type="text"
        value={value[k] ?? ''}
        onChange={(e) => onChange({ ...value, [k]: e.target.value })}
        className="w-full text-sm bg-background border border-border rounded-lg px-3 py-1.5 text-foreground placeholder:text-muted-foreground"
        placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
      />
    );
  }
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        {field('name')}
        {field('company')}
      </div>
      {field('line1')}
      {field('line2')}
      <div className="grid grid-cols-3 gap-2">
        {field('city')}
        {field('state')}
        {field('postcode')}
      </div>
      {field('country')}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function PoPurchaseOrderEdit() {
  const token = useAuthStore((s) => s.token);
  const authOpts = useAuthHeaders();
  const [, setLocation] = useLocation();
  const [, params] = useRoute('/po/purchase-orders/:id/edit');
  const { toast } = useToast();
  const poId = params?.id;

  const { data: me, isLoading: meLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false },
  });

  useEffect(() => {
    if (!meLoading && me && !(me as any).featureFlags?.purchase_orders) {
      setLocation('/');
    }
  }, [meLoading, me]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [supplier, setSupplier] = useState<PoSupplier | null>(null);
  const [poSettings, setPoSettings] = useState<{ logoUrl: string | null; fromName: string | null }>({ logoUrl: null, fromName: null });

  // Editable state
  const [notes, setNotes] = useState('');
  const [shipToAddress, setShipToAddress] = useState<Address>({});
  const [editShipTo, setEditShipTo] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  // ── Load ───────────────────────────────────────────────────────────────────

  async function loadPo() {
    if (!poId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/po/orders/${poId}`, { headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? 'Failed to load PO');
      }
      const data = await res.json();
      const po: PurchaseOrder = data.order;
      const sup: PoSupplier | null = data.supplier ?? null;
      const items: any[] = data.items ?? [];

      setOrder(po);
      setSupplier(sup);
      setNotes(po.notes ?? '');
      setShipToAddress(po.shipToAddress ?? {});
      setLineItems(
        items.length > 0
          ? items.map((it: any) => ({
              id: it.id,
              productTitle: it.productTitle ?? '',
              variantTitle: it.variantTitle ?? '',
              sku: it.sku ?? '',
              quantity: it.quantity ?? 1,
              unitPriceCents: it.unitPriceCents ?? 0,
              _key: nextKey(),
            }))
          : [blankItem()],
      );

      // Load PO branding settings (best-effort — don't fail the whole load)
      try {
        const settingsRes = await fetch(`${BASE}/api/po/settings?storeId=${po.storeId}`, { headers });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setPoSettings({
            logoUrl: settingsData.settings?.logoUrl ?? null,
            fromName: settingsData.settings?.fromName ?? null,
          });
        }
      } catch {
        // Settings load failed — PDF will use default header
      }
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadPo(); }, [poId]);

  // ── Line item helpers ──────────────────────────────────────────────────────

  function updateItem(key: string, field: keyof LineItem, value: any) {
    setLineItems((prev) =>
      prev.map((it) => (it._key === key ? { ...it, [field]: value } : it)),
    );
  }

  function updateItemFields(key: string, fields: Partial<LineItem>) {
    setLineItems((prev) =>
      prev.map((it) => (it._key === key ? { ...it, ...fields } : it)),
    );
  }

  function removeItem(key: string) {
    setLineItems((prev) => prev.filter((it) => it._key !== key));
  }

  function addItem() {
    setLineItems((prev) => [...prev, blankItem()]);
  }

  const grandTotalCents = lineItems.reduce(
    (sum, it) => sum + it.quantity * it.unitPriceCents,
    0,
  );

  // ── Save ───────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaving(true);
    try {
      const cleanItems = lineItems
        .filter((it) => it.productTitle.trim())
        .map((it) => ({
          productTitle: it.productTitle.trim(),
          variantTitle: it.variantTitle.trim() || null,
          sku: it.sku.trim() || null,
          quantity: Math.max(1, it.quantity),
          unitPriceCents: it.unitPriceCents,
        }));

      const res = await fetch(`${BASE}/api/po/orders/${poId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: notes.trim() || null,
          shipToAddress: Object.values(shipToAddress).some(Boolean) ? shipToAddress : null,
          lineItems: cleanItems,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to save');
      toast({ title: 'Purchase order saved' });

      // Update local state with returned items
      const updatedItems: any[] = data.items ?? [];
      setLineItems(
        updatedItems.length > 0
          ? updatedItems.map((it: any) => ({
              id: it.id,
              productTitle: it.productTitle ?? '',
              variantTitle: it.variantTitle ?? '',
              sku: it.sku ?? '',
              quantity: it.quantity ?? 1,
              unitPriceCents: it.unitPriceCents ?? 0,
              _key: nextKey(),
            }))
          : [blankItem()],
      );
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  // ── Send ───────────────────────────────────────────────────────────────────

  async function handleSend() {
    setSending(true);
    try {
      const res = await fetch(`${BASE}/api/po/orders/${poId}/send`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to send purchase order');

      toast({ title: `Purchase order sent to ${order?.supplierEmailSnapshot ?? 'supplier'}` });
      // Update order status locally
      setOrder((prev) => prev ? { ...prev, status: 'sent', sentAt: data.order?.sentAt ?? new Date().toISOString() } : prev);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  }

  // ── Receive ────────────────────────────────────────────────────────────────

  async function handleReceive() {
    setReceiving(true);
    try {
      const res = await fetch(`${BASE}/api/po/orders/${poId}/receive`, {
        method: 'PATCH',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to mark as received');
      toast({ title: 'Purchase order marked as received' });
      setOrder((prev) => prev ? { ...prev, status: 'received', receivedAt: data.order?.receivedAt ?? new Date().toISOString() } : prev);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setReceiving(false);
    }
  }

  // ── Download PDF ───────────────────────────────────────────────────────────

  async function handleDownloadPdf() {
    if (!order) return;
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });

    const pageW = 210;
    const margin = 18;
    const contentW = pageW - margin * 2;
    let y = margin;

    // ── Palette ──────────────────────────────────────────────────────────────
    const colorPrimary: [number, number, number] = [99, 102, 241];   // indigo-500
    const colorText: [number, number, number] = [17, 24, 39];        // gray-900
    const colorMuted: [number, number, number] = [107, 114, 128];    // gray-500
    const colorBorder: [number, number, number] = [229, 231, 235];   // gray-200
    const colorHeaderBg: [number, number, number] = [243, 244, 246]; // gray-100

    // ── Header bar ───────────────────────────────────────────────────────────
    const hasBranding = !!(poSettings.fromName || poSettings.logoUrl);
    const headerBarH = hasBranding ? 20 : 14;

    doc.setFillColor(...colorPrimary);
    doc.rect(0, 0, pageW, headerBarH, 'F');
    doc.setTextColor(255, 255, 255);

    // Try to embed merchant logo via server-side proxy (avoids CORS blocks on the image host)
    let logoEndX = margin;
    if (poSettings.logoUrl) {
      try {
        const proxyUrl = `${BASE}/api/po/proxy-image?url=${encodeURIComponent(poSettings.logoUrl)}`;
        const imgResult = await (async () => {
          const r = await fetch(proxyUrl, { headers });
          if (!r.ok) {
            const errData = await r.json().catch(() => ({}));
            throw new Error((errData as any).error ?? `Logo fetch failed (${r.status})`);
          }
          const blob = await r.blob();
          const format: 'PNG' | 'JPEG' = blob.type.includes('png') ? 'PNG' : 'JPEG';
          const dataUrl: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('FileReader error'));
            reader.readAsDataURL(blob);
          });
          return { dataUrl, format };
        })();
        if (imgResult) {
          const imgProps = doc.getImageProperties(imgResult.dataUrl);
          const maxLogoH = headerBarH - 5;
          const maxLogoW = 52;
          const aspectRatio = imgProps.width / imgProps.height;
          const logoH = Math.min(maxLogoH, maxLogoW / aspectRatio);
          const logoW = Math.min(maxLogoW, logoH * aspectRatio);
          const logoY = (headerBarH - logoH) / 2;
          doc.addImage(imgResult.dataUrl, imgResult.format, margin, logoY, logoW, logoH);
          logoEndX = margin + logoW + 4;
        }
      } catch (logoErr: any) {
        // Logo failed to load — show a warning and fall through to text-only header
        toast({
          title: 'Logo not included in PDF',
          description: logoErr?.message ?? 'The logo could not be loaded. The PDF will use a text-only header.',
          variant: 'destructive',
        });
      }
    }

    // Business name / label
    doc.setFont('helvetica', 'bold');
    const midY = headerBarH / 2;
    if (poSettings.fromName) {
      doc.setFontSize(12);
      doc.text(poSettings.fromName, logoEndX, midY + (hasBranding ? 1.5 : 2));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(220, 221, 255); // light indigo tint
      doc.text('PURCHASE ORDER', logoEndX, midY + 6.5);
      doc.setTextColor(255, 255, 255);
    } else if (logoEndX > margin) {
      // Logo only — show PO label next to logo
      doc.setFontSize(11);
      doc.text('PURCHASE ORDER', logoEndX, midY + 2);
    } else {
      // No branding — default
      doc.setFontSize(13);
      doc.text('PURCHASE ORDER', margin, midY + 2);
    }

    // PO number right-aligned in header
    const poLabel = order.poNumber;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    const poLabelW = doc.getTextWidth(poLabel);
    doc.text(poLabel, pageW - margin - poLabelW, midY + 2);

    y = headerBarH + 8;

    // ── Date & status row ────────────────────────────────────────────────────
    doc.setTextColor(...colorMuted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const dateStr = `Date: ${formatDate(order.createdAt)}`;
    doc.text(dateStr, margin, y);
    if (order.sentAt) {
      doc.text(`Sent: ${formatDate(order.sentAt)}`, margin + 60, y);
    }
    if (order.sourceOrderName) {
      doc.text(`Source Order: ${order.sourceOrderName}`, margin + 120, y);
    }
    y += 8;

    // ── Supplier / Ship-to grid ───────────────────────────────────────────────
    const colW = (contentW - 6) / 2;

    function drawAddressBlock(
      title: string,
      lines: string[],
      x: number,
      startY: number,
    ): number {
      const blockH = 6 + lines.length * 5 + 4;
      doc.setFillColor(...colorHeaderBg);
      doc.roundedRect(x, startY, colW, blockH, 2, 2, 'F');
      doc.setDrawColor(...colorBorder);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, startY, colW, blockH, 2, 2, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...colorMuted);
      doc.text(title.toUpperCase(), x + 3, startY + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      let ly = startY + 10;
      for (const line of lines) {
        doc.setTextColor(...colorText);
        doc.text(line, x + 3, ly, { maxWidth: colW - 6 });
        ly += 5;
      }
      return startY + blockH;
    }

    const supplierLines: string[] = [effectiveSupplierName ?? ''];
    if (effectiveSupplierEmail) supplierLines.push(effectiveSupplierEmail);
    if (effectiveSupplierPhone) supplierLines.push(effectiveSupplierPhone);
    if (effectiveSupplierAddress) {
      const addr = addressOneLine(effectiveSupplierAddress);
      if (addr) supplierLines.push(addr);
    }

    const shipLines: string[] = [];
    if (isDropship) {
      shipLines.push('Customer address (Dropship)');
    } else if (Object.values(shipToAddress).some(Boolean)) {
      if (shipToAddress.name) shipLines.push(shipToAddress.name);
      if (shipToAddress.company) shipLines.push(shipToAddress.company);
      const addr = addressOneLine(shipToAddress);
      if (addr) shipLines.push(addr);
    } else {
      shipLines.push("Merchant's default address");
    }

    const leftX = margin;
    const rightX = margin + colW + 6;

    const supplierEndY = drawAddressBlock('Supplier', supplierLines, leftX, y);
    const shipEndY = drawAddressBlock('Ship To', shipLines, rightX, y);
    y = Math.max(supplierEndY, shipEndY) + 8;

    // ── Line items table ──────────────────────────────────────────────────────
    const colProduct = margin;
    const colVariant = margin + 60;
    const colSku = margin + 102;
    const colQty = margin + 134;
    const colUnit = margin + 148;
    const colTotal = margin + 163;

    // Table header
    doc.setFillColor(...colorHeaderBg);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setDrawColor(...colorBorder);
    doc.setLineWidth(0.3);
    doc.rect(margin, y, contentW, 8, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...colorMuted);
    doc.text('Product', colProduct + 2, y + 5.2);
    doc.text('Variant', colVariant + 2, y + 5.2);
    doc.text('SKU', colSku + 2, y + 5.2);
    doc.text('Qty', colQty + 2, y + 5.2);
    doc.text('Unit Price', colUnit + 2, y + 5.2);
    doc.text('Total', colTotal + 2, y + 5.2);
    y += 8;

    // Rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      const rowH = 7;
      if (i % 2 === 1) {
        doc.setFillColor(250, 250, 252);
        doc.rect(margin, y, contentW, rowH, 'F');
      }
      doc.setDrawColor(...colorBorder);
      doc.setLineWidth(0.2);
      doc.line(margin, y + rowH, margin + contentW, y + rowH);

      doc.setTextColor(...colorText);
      // Truncate long text
      const maxProd = 38;
      const prodText = item.productTitle.length > maxProd
        ? item.productTitle.slice(0, maxProd - 1) + '…'
        : item.productTitle;
      doc.text(prodText, colProduct + 2, y + 4.8);

      const varText = (item.variantTitle || '—').slice(0, 22);
      doc.setTextColor(...colorMuted);
      doc.text(varText, colVariant + 2, y + 4.8);

      const skuText = (item.sku || '—').slice(0, 18);
      doc.text(skuText, colSku + 2, y + 4.8);

      doc.setTextColor(...colorText);
      doc.text(String(item.quantity), colQty + 2, y + 4.8);
      doc.text(`$${centsToDisplay(item.unitPriceCents)}`, colUnit + 2, y + 4.8);

      const totalStr = `$${centsToDisplay(item.quantity * item.unitPriceCents)}`;
      const totalW = doc.getTextWidth(totalStr);
      doc.text(totalStr, margin + contentW - 2 - totalW, y + 4.8);

      y += rowH;

      // Page break guard
      if (y > 265 && i < lineItems.length - 1) {
        doc.addPage();
        y = margin;
      }
    }

    // Grand total row
    y += 2;
    doc.setFillColor(...colorPrimary);
    doc.rect(margin, y, contentW, 9, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('Grand Total', colUnit, y + 6);
    const gtStr = `$${centsToDisplay(grandTotalCents)}`;
    const gtW = doc.getTextWidth(gtStr);
    doc.text(gtStr, margin + contentW - 2 - gtW, y + 6);
    y += 14;

    // ── Notes ─────────────────────────────────────────────────────────────────
    if (order.notes) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...colorMuted);
      doc.text('NOTES', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...colorText);
      const noteLines = doc.splitTextToSize(order.notes, contentW);
      doc.text(noteLines, margin, y);
      y += noteLines.length * 5 + 4;
    }

    // ── Footer ────────────────────────────────────────────────────────────────
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...colorMuted);
      doc.text(
        `${order.poNumber} · Generated ${new Date().toLocaleDateString('en-AU')} · Page ${p} of ${pageCount}`,
        margin,
        295,
      );
    }

    doc.save(`${order.poNumber}.pdf`);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center text-muted-foreground text-sm">
          Purchase order not found.
        </div>
      </Layout>
    );
  }

  const effectiveSupplierName = supplier?.name ?? order.supplierNameSnapshot ?? 'Deleted Supplier';
  const effectiveSupplierEmail = supplier?.email ?? order.supplierEmailSnapshot ?? '—';
  const effectiveSupplierPhone = supplier?.phone ?? null;
  const effectiveSupplierAddress = supplier?.supplierAddress ?? order.supplierAddressSnapshot ?? null;
  const isDropship = supplier?.dropshipMode ?? false;
  const isDraft = order.status === 'draft';
  const isSent = order.status === 'sent';
  const isReceived = order.status === 'received';
  const hasSupplierEmail = !!(supplier?.email ?? order.supplierEmailSnapshot);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLocation('/po/purchase-orders')}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {order.poNumber}
              </h1>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                order.status === 'draft'
                  ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
                  : order.status === 'sent'
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                  : order.status === 'received'
                  ? 'bg-green-600/15 text-green-400 border border-green-600/20'
                  : 'bg-muted text-muted-foreground border border-border'
              }`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Created {formatDate(order.createdAt)}
              {order.sourceOrderName && ` · Order ${order.sourceOrderName}`}
              {isSent && order.sentAt && ` · Sent ${formatDate(order.sentAt)}`}
              {isReceived && order.receivedAt && ` · Received ${formatDate(order.receivedAt)}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Download PDF button */}
            <button
              onClick={handleDownloadPdf}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
              title="Download as PDF"
            >
              <Download className="w-3.5 h-3.5" />
              Download PDF
            </button>
            {/* Send / Resend button */}
            {(isDraft || isSent) && hasSupplierEmail && (
              <button
                onClick={handleSend}
                disabled={sending}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-50 ${
                  isSent
                    ? 'border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                    : 'border-primary/40 text-primary hover:bg-primary/10'
                }`}
              >
                {sending
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : isSent
                  ? <RefreshCw className="w-3.5 h-3.5" />
                  : <Send className="w-3.5 h-3.5" />
                }
                {isSent ? 'Resend' : 'Send to Supplier'}
              </button>
            )}
            {/* Mark as Received button */}
            {isSent && (
              <button
                onClick={handleReceive}
                disabled={receiving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-green-600/40 text-green-400 hover:bg-green-600/10 disabled:opacity-50 transition-colors"
              >
                {receiving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                Mark as Received
              </button>
            )}
            {isDraft && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save Draft
              </button>
            )}
          </div>
        </div>

        {/* Sent info banner */}
        {isSent && order.sentAt && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center gap-2 text-xs text-emerald-400">
            <Send className="w-3.5 h-3.5 shrink-0" />
            Sent to {effectiveSupplierEmail} on {formatDate(order.sentAt)}
          </div>
        )}

        {/* Received info banner */}
        {isReceived && order.receivedAt && (
          <div className="bg-green-600/10 border border-green-600/20 rounded-xl p-3 flex items-center gap-2 text-xs text-green-400">
            <CheckCircle className="w-3.5 h-3.5 shrink-0" />
            Received on {formatDate(order.receivedAt)} — this purchase order is now closed.
          </div>
        )}

        {/* Deleted supplier warning */}
        {order.supplierDeletedNote && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2 text-xs text-amber-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {order.supplierDeletedNote}
          </div>
        )}

        {/* No email warning for draft */}
        {isDraft && !hasSupplierEmail && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2 text-xs text-amber-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            This PO has no supplier email address. Add an email to the supplier to enable sending.
          </div>
        )}

        {/* Supplier + Ship-to blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Supplier block */}
          <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Supplier</h3>
            <p className="text-sm font-medium text-foreground">{effectiveSupplierName}</p>
            {effectiveSupplierEmail && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Mail className="w-3 h-3 shrink-0" />
                {effectiveSupplierEmail}
              </p>
            )}
            {effectiveSupplierPhone && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Phone className="w-3 h-3 shrink-0" />
                {effectiveSupplierPhone}
              </p>
            )}
            {effectiveSupplierAddress && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                {addressOneLine(effectiveSupplierAddress)}
              </p>
            )}
          </div>

          {/* Ship-to block */}
          <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Ship To {isDropship && <span className="text-primary ml-1">(Dropship)</span>}
              </h3>
              {isDraft && !isDropship && (
                <button
                  onClick={() => setEditShipTo((v) => !v)}
                  className="text-xs text-primary hover:underline"
                >
                  {editShipTo ? 'Done' : 'Edit'}
                </button>
              )}
            </div>
            {isDropship ? (
              <p className="text-xs text-muted-foreground">Customer address will be used for this dropship supplier.</p>
            ) : editShipTo ? (
              <AddressEditor
                value={shipToAddress}
                onChange={setShipToAddress}
                label=""
              />
            ) : (
              <>
                {Object.values(shipToAddress).some(Boolean) ? (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                    {addressOneLine(shipToAddress)}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    Using merchant's default address.{' '}
                    {isDraft && (
                      <button onClick={() => setEditShipTo(true)} className="text-primary hover:underline not-italic">
                        Override
                      </button>
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Line items */}
        <div className="bg-muted/30 border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Line Items</h3>
            {isDraft && (
              <button
                onClick={addItem}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Plus className="w-3 h-3" />
                Add row
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20 text-left">
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Product</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Variant</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">SKU</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground w-20">Qty</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground w-28">Unit Price</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground w-28 text-right">Total</th>
                  {isDraft && <th className="w-8" />}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item) => (
                  <tr key={item._key} className="border-b border-border last:border-0">
                    {isDraft ? (
                      <>
                        <td className="px-2 py-1.5">
                          <ProductSearchCombobox
                            value={item.productTitle}
                            storeId={order.storeId}
                            token={token}
                            onChange={(val) => updateItem(item._key!, 'productTitle', val)}
                            onSelect={(productTitle, variantTitle, sku) =>
                              updateItemFields(item._key!, { productTitle, variantTitle, sku })
                            }
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            value={item.variantTitle}
                            onChange={(e) => updateItem(item._key!, 'variantTitle', e.target.value)}
                            placeholder="Variant"
                            className="w-full text-sm bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1.5 py-1 text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            value={item.sku}
                            onChange={(e) => updateItem(item._key!, 'sku', e.target.value)}
                            placeholder="SKU"
                            className="w-full text-sm bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1.5 py-1 text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={(e) => updateItem(item._key!, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-full text-sm bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1.5 py-1 text-foreground outline-none transition-colors text-right"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="relative">
                            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                            <input
                              type="text"
                              value={centsToDisplay(item.unitPriceCents)}
                              onChange={(e) => updateItem(item._key!, 'unitPriceCents', displayToCents(e.target.value))}
                              onBlur={(e) => updateItem(item._key!, 'unitPriceCents', displayToCents(e.target.value))}
                              className="w-full text-sm bg-transparent border border-transparent hover:border-border focus:border-primary rounded pl-5 pr-1.5 py-1 text-foreground outline-none transition-colors text-right"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs text-foreground">
                          ${centsToDisplay(item.quantity * item.unitPriceCents)}
                        </td>
                        <td className="px-1 py-1.5">
                          <button
                            onClick={() => removeItem(item._key!)}
                            className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-foreground">{item.productTitle}</td>
                        <td className="px-3 py-2 text-muted-foreground">{item.variantTitle || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground font-mono text-xs">{item.sku || '—'}</td>
                        <td className="px-3 py-2 text-foreground text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-foreground text-right">${centsToDisplay(item.unitPriceCents)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-foreground">
                          ${centsToDisplay(item.quantity * item.unitPriceCents)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {lineItems.length === 0 && (
                  <tr>
                    <td colSpan={isDraft ? 7 : 6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No line items yet. {isDraft && 'Click "Add row" to add products.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Grand total */}
          <div className="flex justify-end px-4 py-3 border-t border-border">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Grand Total</span>
              <span className="text-base font-semibold text-foreground font-mono">
                ${centsToDisplay(grandTotalCents)}
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Notes</label>
          {isDraft ? (
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Special instructions, delivery notes, etc."
              className="w-full text-sm bg-muted/30 border border-border rounded-xl px-3 py-2 text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-primary transition-colors"
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {order.notes ?? <span className="italic">No notes.</span>}
            </p>
          )}
        </div>

        {/* Bottom save button */}
        {isDraft && (
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Draft
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
